(() => {
  if (customElements.get('jf-omont-viewer')) return;
  let featurePromise;
  const loadViewer = () => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!featurePromise) {
      featurePromise = new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Viewer unavailable')), 18000);
        if (!window.Shopify?.loadFeatures) {
          clearTimeout(timer);
          reject(new Error('Viewer unavailable'));
          return;
        }
        window.Shopify.loadFeatures([{
          name: 'model-viewer', version: '1.12',
          onLoad: errors => {
            if (errors) { clearTimeout(timer); reject(errors); return; }
            customElements.whenDefined('model-viewer').then(() => { clearTimeout(timer); resolve(); });
          }
        }]);
      }).catch(error => { featurePromise = null; throw error; });
    }
    return featurePromise;
  };
  customElements.define('jf-omont-viewer', class extends HTMLElement {
    static get observedAttributes() { return ['main-color', 'trim-color', 'facing', 'active']; }
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.text = JSON.parse(this.querySelector('[data-viewer-text]').textContent);
      this.status = this.querySelector('[data-viewer-status]');
      this.addEventListener('click', event => {
        const angle = event.target.closest('[data-camera]');
        if (angle) this.setCamera(angle.dataset.camera);
        if (event.target.closest('[data-viewer-retry]')) { this.failed = false; this.activate(); }
      }, { signal: this.controller.signal });
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.tryActivate();
      });
      this.intersectionObserver.observe(this);
      this.resizeObserver = new ResizeObserver(() => {
        if (this.modelReady && this.hasAttribute('active')) this.setCamera(this.cameraName || 'hero', false);
      });
      this.resizeObserver.observe(this.querySelector('.jf-viewer-stage'));
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.resizeObserver?.disconnect(); this.intersectionObserver?.disconnect();
    }
    attributeChangedCallback(name) {
      if (name === 'active') this.tryActivate();
      else this.updateModel();
    }
    tryActivate() {
      if (this.controller && this.inView && this.hasAttribute('active') && !this.modelReady && !this.loading && !this.failed) this.activate();
    }
    async activate() {
      if (this.loading || !this.isConnected) return;
      this.loading = true;
      this.dataset.loadState = 'loading';
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = this.text.loading;
      this.querySelector('[data-viewer-loading]').hidden = false;
      this.querySelector('[data-viewer-retry]').hidden = true;
      try {
        await loadViewer();
        if (!this.isConnected) return;
        this.viewer?.remove();
        const viewer = document.createElement('model-viewer');
        this.viewer = viewer;
        const attributes = {
          src: this.dataset.source, alt: this.text.alt, 'camera-controls': '', 'touch-action': 'pan-y',
          'shadow-intensity': '0.8', 'shadow-softness': '1', 'environment-image': 'neutral', exposure: '1',
          'field-of-view': '32deg', 'camera-target': this.dataset.cameraTarget || '0m 0.43m -0.68m', 'min-field-of-view': '20deg',
          'max-field-of-view': '45deg', 'interaction-prompt': 'none', 'camera-orbit': '28deg 65deg 7.5m',
          'min-camera-orbit': `auto 25deg ${this.dataset.cameraMinDistance || 3}m`, 'max-camera-orbit': 'auto 85deg 18m'
        };
        for (const [key, value] of Object.entries(attributes)) viewer.setAttribute(key, value);
        viewer.tabIndex = 0;
        const loaded = new Promise((resolve, reject) => {
          const timer = setTimeout(() => reject(new Error('Model unavailable')), 45000);
          viewer.addEventListener('load', () => { clearTimeout(timer); resolve(); }, { once: true });
          viewer.addEventListener('error', () => { clearTimeout(timer); reject(new Error('Model unavailable')); }, { once: true });
        });
        this.querySelector('.jf-viewer-mount').append(viewer);
        await loaded;
        if (!this.isConnected) return;
        this.modelReady = true;
        this.failed = false;
        this.updateModel();
        this.setCamera('hero', false);
        this.querySelector('[data-viewer-loading]').hidden = true;
        this.querySelector('.jf-viewer-angles').hidden = false;
        this.status.textContent = this.text.ready;
        this.dataset.loadState = 'ready';
      } catch (error) {
        console.warn('[Jubilee 3D]', error);
        this.viewer?.remove(); this.viewer = null; this.modelReady = false; this.failed = true;
        this.dataset.loadState = 'error';
        this.querySelector('[data-viewer-loading]').hidden = true;
        this.querySelector('.jf-viewer-angles').hidden = true;
        this.querySelector('[data-viewer-retry]').hidden = false;
        this.status.textContent = this.text.failed;
      } finally {
        this.loading = false;
        this.removeAttribute('aria-busy');
      }
    }
    updateModel() {
      if (!this.modelReady || !this.viewer?.model) return;
      if (this.hasAttribute('data-preserve-materials')) return;
      for (const material of this.viewer.model.materials) {
        const role = material.name === 'JUBILEE Main Color' ? 'main' : material.name === 'JUBILEE Trim Color' ? 'trim' : null;
        if (!role) continue;
        const hex = this.getAttribute(`${role}-color`) || '#b0b0ac';
        const color = [1, 3, 5].map(offset => {
          const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
          return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
        });
        material.pbrMetallicRoughness.setBaseColorFactor([...color, 1]);
        material.pbrMetallicRoughness.setRoughnessFactor(role === 'main' ? 0.57 : 0.51);
      }
      const scale = this.getAttribute('facing') === 'left' ? '-1 1 1' : '1 1 1';
      if (this.viewer.getAttribute('scale') !== scale) {
        this.viewer.setAttribute('scale', scale);
        this.viewer.updateFraming?.();
        if (this.hasAttribute('data-camera-follow-facing')) this.setCamera(this.cameraName || 'hero', false);
      }
    }
    setCamera(name, announce = true) {
      const stage = this.querySelector('.jf-viewer-stage');
      const aspect = stage.clientWidth / Math.max(1, stage.clientHeight);
      const radius = Math.max(Number(this.dataset.cameraDistance) || 7.5, (Number(this.dataset.cameraSpan) || 6.4) / (2 * Math.tan(16 * Math.PI / 180) * aspect));
      const heroAngle = this.hasAttribute('data-camera-follow-facing') && this.getAttribute('facing') === 'left' ? -28 : 28;
      const angles = { hero: `${heroAngle}deg 65deg ${radius}m`, front: `0deg 72deg ${radius}m`, back: `180deg 65deg ${radius}m` };
      if (!this.viewer || !angles[name]) return;
      this.cameraName = name;
      this.viewer.setAttribute('camera-orbit', angles[name]);
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) this.viewer.jumpCameraToGoal?.();
      this.querySelectorAll('[data-camera]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.camera === name)));
      if (announce) this.status.textContent = this.querySelector(`[data-camera="${name}"]`).textContent + ' · ' + this.text.ready;
    }
  });
})();
